import {
  ErrorBoundary as ReactErrorBoundary,
  FallbackProps,
} from 'react-error-boundary';
import { isAxiosError } from 'axios';
import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import { Text } from '@antimatterhq/ui/components/typography';
import { Button } from '@antimatterhq/ui/components/button';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<FallbackProps>;
  onError?: (error: Error, handled: boolean) => void;
}

export const DefaultErrorBoundaryFallback = ({
  error,
  resetErrorBoundary,
}: FallbackProps) => {
  const errorMessage = isAxiosError(error)
    ? (error.response?.statusText ?? error.message)
    : error.message;

  return (
    <div className="flex w-full flex-col items-center justify-center gap-4">
      <Text size="lg">{errorMessage}</Text>
      <Button onClick={resetErrorBoundary}>Retry</Button>
    </div>
  );
};

export function ErrorBoundary(props: ErrorBoundaryProps) {
  const { reset } = useQueryErrorResetBoundary();

  const onError = (error: Error) => {
    if (props.onError) {
      props.onError(error, false);
    }
  };

  return (
    <ReactErrorBoundary
      FallbackComponent={props.fallback || DefaultErrorBoundaryFallback}
      onError={onError}
      onReset={reset}
    >
      {props.children}
    </ReactErrorBoundary>
  );
}
